import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { CourseHubShell } from '@/components/courses/CourseHubShell';
import { CourseForumEmptyIllustration } from '@/features/course-forum/CourseForumEmptyIllustration';
import { CourseForumAliasAside } from '@/features/course-forum/CourseForumAliasAside';
import { FORUM_PAGE_DESCRIPTION } from '@/features/course-forum/constants';
import { CourseForumHelpModal } from '@/features/course-forum/CourseForumHelpModal';
import { CourseForumNewThreadModal } from '@/features/course-forum/CourseForumNewThreadModal';
import { CourseForumThreadBoard } from '@/features/course-forum/CourseForumThreadBoard';
import { type ForumOverview } from '@/features/course-forum/types';
import { api } from '@/lib/api';
import { forumThreadCreateSuccessToast } from '@/lib/user-feedback';

interface StudentCourseMeta {
  name: string;
  title?: string;
}

type SortOrder = 'desc' | 'asc';

export function CourseForumPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [threadModalOpen, setThreadModalOpen] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const { data: course } = useQuery({
    queryKey: ['student', 'courses', id],
    queryFn: () => api<StudentCourseMeta>(`/student/courses/${id}`),
    enabled: Boolean(id),
  });

  const { data: forum, isLoading, error } = useQuery({
    queryKey: ['student', 'courses', id, 'forum'],
    queryFn: () => api<ForumOverview>(`/student/courses/${id}/forum`),
    enabled: Boolean(id),
  });

  const createMutation = useMutation({
    mutationFn: (payload: { title: string; body: string }) =>
      api<{ id: string }>(`/student/courses/${id}/forum/threads`, {
        method: 'POST',
        body: payload,
      }),
    onSuccess: async (thread) => {
      setThreadModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['student', 'courses', id, 'forum'] });
      toast(forumThreadCreateSuccessToast());
      if (thread?.id) {
        navigate(`/mis-cursos/${id}/foro/${thread.id}`);
      }
    },
  });

  const threads = useMemo(() => {
    const query = search.trim().toLowerCase();
    let list = [...(forum?.threads ?? [])];

    if (query) {
      list = list.filter(
        (thread) =>
          thread.title.toLowerCase().includes(query) ||
          thread.excerpt.toLowerCase().includes(query) ||
          thread.author.displayName.toLowerCase().includes(query) ||
          (thread.author.anonAlias?.toLowerCase().includes(query) ?? false),
      );
    }

    list.sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      const delta = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      return sortOrder === 'desc' ? delta : -delta;
    });

    return list;
  }, [forum?.threads, search, sortOrder]);

  if (!id) return null;

  const courseName = course?.name ?? course?.title ?? 'Curso';
  const hasThreads = (forum?.threads?.length ?? 0) > 0;

  return (
    <CourseHubShell
      courseId={id}
      courseName={courseName}
      activeTab="foro"
      description={FORUM_PAGE_DESCRIPTION}
      aside={forum?.myAnonAlias ? <CourseForumAliasAside alias={forum.myAnonAlias} /> : undefined}
      studentHub
    >
      <div className="lms-course-forum-page">
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {(error as Error).message}
          </p>
        ) : null}

        {isLoading || !forum ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : !forum.enabled ? (
          <div className="lms-course-forum-disabled">
            El foro está deshabilitado para este curso.
          </div>
        ) : (
          <>
            {!hasThreads ? (
              <div className="lms-forum-flow">
                <div className="lms-forum-flow__bar">
                  <h2 className="lms-course-forum-heading">Foros</h2>
                  <button
                    type="button"
                    className="lms-course-forum-btn lms-course-forum-btn--primary"
                    onClick={() => setThreadModalOpen(true)}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      add
                    </span>
                    + Nuevo hilo
                  </button>
                </div>
                <div className="lms-course-forum-empty-state">
                  <div className="lms-course-forum-empty-state__glow lms-course-forum-empty-state__glow--top" aria-hidden="true" />
                  <div className="lms-course-forum-empty-state__glow lms-course-forum-empty-state__glow--bottom" aria-hidden="true" />
                  <CourseForumEmptyIllustration />
                  <h2 className="lms-course-forum-empty-state__title">
                    Aún no hay hilos en este foro. Sé el primero en publicar.
                  </h2>
                  <p className="lms-course-forum-empty-state__lead">
                    Formula dudas conceptuales sobre las unidades, comparte recursos de apoyo o inicia un
                    debate académico con tus compañeros y docentes.
                  </p>
                  <div className="lms-course-forum-empty-state__actions">
                    <button
                      type="button"
                      className="lms-course-forum-btn lms-course-forum-btn--accent"
                      onClick={() => setThreadModalOpen(true)}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        add_comment
                      </span>
                      Crear el primer hilo
                    </button>
                    <button
                      type="button"
                      className="lms-course-forum-btn lms-course-forum-btn--ghost"
                      onClick={() => setHelpModalOpen(true)}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        help
                      </span>
                      ¿Cómo participar?
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <CourseForumThreadBoard
                title="Foros"
                threads={threads}
                courseId={id}
                search={search}
                onSearchChange={setSearch}
                sortOrder={sortOrder}
                onToggleSort={() => setSortOrder((value) => (value === 'desc' ? 'asc' : 'desc'))}
                onNewThread={() => setThreadModalOpen(true)}
                newThreadLabel="+ Nuevo hilo"
                emptyMessage="No hay hilos que coincidan con tu búsqueda o filtros."
              />
            )}
          </>
        )}
      </div>

      <CourseForumNewThreadModal
        open={threadModalOpen}
        saving={createMutation.isPending}
        submitError={createMutation.error instanceof Error ? createMutation.error.message : null}
        onClose={() => {
          setThreadModalOpen(false);
          createMutation.reset();
        }}
        onSubmit={(payload) => createMutation.mutate(payload)}
      />
      <CourseForumHelpModal open={helpModalOpen} onClose={() => setHelpModalOpen(false)} />
    </CourseHubShell>
  );
}
